import {
  Backdrop,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CategoryMaster,
  ItemMaster,
  TypeMaster,
} from '../../../services/apiTypes';
import {
  HEADER_HEIGHT,
  LOADING_BACKDROP_STYLE,
  MANAGE_ITEM_LEFT_POSITION,
  TABLE_HEADER,
  TEXT_OVERFLOW_1LINE,
} from '../../../_constants/styleConst';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../stores/store';
import { CategoryRow } from './InspectionItemTableRows/CategoryRow';
import { closestCenter, DndContext } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { theme } from '../../../theme';
import { Options } from '../ManageInspectionItem';
import EditCategoryModal from './Modals/EditCategoryModal';
import EditItemModal from './Modals/EditItemLabelModal';
import EditTypeModal from './Modals/EditTypeModal';
import {
  isAdmin as isAdministrator,
  isAreaManager,
} from '../../../_utils/utils';
import { Add } from '@mui/icons-material';
import useErrorModal from '../../../hooks/useErrorModal';
import ErrorModal from '../../../_common/Modal/ErrorModal';
import ConfirmModal from '../../../_common/Modal/ConfirmModal';
import {
  MANAGE_ITEM_TEMP_ID,
  MANAGE_ITEM_MODE,
  MANAGE_ITEM_ROW_TYPE,
} from '../../../_constants/manageConst';
import MessagePFRModal from './Modals/MessagePFRModal';
import useCategory from '../hooks/useCategory';
import useType from '../hooks/useType';
import useItem from '../hooks/useItem';
import useSortItem from '../hooks/useSortItem';
import TypeItems from './InspectionItemTableRows/TypeItems/TypeItems';

/** スタイル */
export const manageItemStyle = {
  /** ボタンの基本設定 */
  button: { p: '0.4rem 1rem', minWidth: '150px' },
  /** テーブルヘッダー固定(縦スクロール) */
  headerSticky: { position: 'sticky', top: 0, zIndex: 100 },
  /** tableBodyの設定 */
  tableStyle: {
    '& tr:first-of-type td': { borderTop: 'initial' },
    borderCollapse: 'separate',
    width: '100%',
    tableLayout: 'auto',
  },
  /** 大項目の二重線 */
  doubleBorder: {
    '& td': {
      borderTop: `double ${theme.palette.grey[400]}`,
      overflowWrap: 'break-word',
    },
  },
  /** 項目名固定(横スクロール) */
  leftSideSticky: {
    backgroundColor: theme.palette.background.default,
    zIndex: 10,
    position: 'sticky',
  },
  /** 操作ボタン固定（左から順に） */
  icon1Sticky: {
    minWidth: '35px',
    backgroundColor: theme.palette.background.default,
    zIndex: 96,
    position: 'sticky',
    right: 69,
  },
  icon2Sticky: {
    minWidth: '35px',
    backgroundColor: theme.palette.background.default,
    zIndex: 96,
    position: 'sticky',
    right: 34,
  },
  icon3Sticky: {
    minWidth: '35px',
    backgroundColor: theme.palette.background.default,
    zIndex: 96,
    position: 'sticky',
    right: 0,
  },
  /** アコーディオンアイコンと項目名を並べるBoxの設定 */
  nameBox: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
  },
  /** 項目名のtypographyの設定 */
  nameTypography: {
    width: '100%',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    display: 'inline-block',
  },
  /** 編集中操作ボタンの固定 */
  iconEditingSticky: {
    position: 'sticky',
    right: 0,
    zIndex: 98,
    backgroundColor: '#FFFFFF',
  },
  /** 編集中のアイコンの基本設定 */
  iconEditing: {
    mx: '0.5rem',
    p: 0,
    minWidth: 0,
    backgroundColor: '#FFFFFF',
    borderRadius: '10%',
    width: '35px',
    height: '35px',
    boxShadow:
      '0 2px 2px 0 #cfcfcf, 0 1px 5px 0 #cfcfcf, 0 3px 1px -2px #cfcfcf',
    ':hover': {
      backgroundColor: '#e0e0e0',
      transition: 'background-color 0.3s ease',
    },
  },
  /** 編集OKのアイコンと枠の色 */
  editOk: {
    color: theme.palette.primary.main,
    border: `${theme.palette.primary.main} solid 1px`,
  },
  /** 編集キャンセルのアイコンと枠の色 */
  editCancel: {
    color: theme.palette.error.main,
    border: `${theme.palette.error.main} solid 1px`,
  },
  /** ボタン非活性時の枠 */
  editDisabled: {
    border: `${theme.palette.grey[400]} solid 1px`,
  },
  /** アイコンのあるセル(中央寄せ) */
  iconCellCenter: {
    p: 0,
    textAlign: 'center',
    width: '35px',
    minWidth: '35px',
    minHeight: '30px',
  },
  /** アイコンのあるセル(右寄せ) */
  iconCellRight: {
    p: 0,
    textAlign: 'right',
    width: '35px',
    minWidth: '35px',
    minHeight: '30px',
    verticalAlign: 'top',
  },
  /** アイコン分の空箱 */
  emptyBox: {
    width: '35px',
    display: 'inline-block',
  },
};

/** テーブルのヘッダー */
const adminTableHeaders = [
  {
    minWidth: '35px',
    sticky: true,
    left: 0,
  },
  {
    minWidth: '70px',
    label: 'LBL_MANAGE_INSPECTION_CATEGORY',
    textAlign: 'center',
    sticky: true,
    left: 1,
  },
  {
    minWidth: '200px',
    label: 'LBL_MANAGE_INSPECTION_TYPE',
    textAlign: 'center',
    sticky: true,
    left: 2,
  },
  {
    minWidth: '300px',
    label: 'LBL_MANAGE_INSPECTION_ITEM',
    textAlign: 'center',
    colSpan: 3,
    sticky: true,
    left: 3,
    px: 0,
  },
  {
    minWidth: '125px',
    label: 'LBL_MANAGE_INSPECTION_OPTIONAL_ADDITION',
    textAlign: 'center',
    wordBreak: true,
    px: 0,
  },
  {
    minWidth: '90px',
    label: 'LBL_MANAGE_INSPECTION_NUMBER_INPUT',
    textAlign: 'center',
    px: 0,
  },
  {
    minWidth: '90px',
    label: 'LBL_MANAGE_INSPECTION_P_CODE',
    textAlign: 'center',
  },
  {
    minWidth: '90px',
    label: 'LBL_MANAGE_INSPECTION_F_CODE',
    textAlign: 'center',
  },
  {
    minWidth: '200px',
    label: 'LBL_MANAGE_INSPECTION_R_CODE',
  },
  {
    minWidth: '200px',
    label: 'LBL_MANAGE_INSPECTION_ATTRIBUTE1',
    wordBreak: true,
  },
  {
    minWidth: '200px',
    label: 'LBL_MANAGE_INSPECTION_ATTRIBUTE2',
    wordBreak: true,
  },
  {
    minWidth: '200px',
    label: 'LBL_MANAGE_INSPECTION_ATTRIBUTE3',
    wordBreak: true,
  },
  {
    minWidth: '200px',
    label: 'LBL_MANAGE_INSPECTION_ATTRIBUTE5',
    wordBreak: true,
  },
  {
    minWidth: '200px',
    label: 'LBL_MANAGE_INSPECTION_ATTRIBUTE6',
    wordBreak: true,
  },
  {
    minWidth: '200px',
    label: 'LBL_MANAGE_INSPECTION_BRAND_NAME',
  },
  {
    minWidth: '100px',
    label: 'LBL_MANAGE_INSPECTION_CONDITION',
    textAlign: 'center',
  },
  {
    minWidth: '350px',
    label: 'LBL_MANAGE_INSPECTION_CRITERIA_TEXT',
  },
  {
    minWidth: '100px',
    label: 'LBL_MANAGE_INSPECTION_CRITERIA_MIN',
  },
  {
    minWidth: '100px',
    label: 'LBL_MANAGE_INSPECTION_CRITERIA_MAX',
  },
  { minWidth: '650px', label: 'LBL_MANAGE_INSPECTION_MEMO' },
  {
    minWidth: '35px',
    textAlign: 'center',
    sticky: true,
    right: 69,
  },
  {
    minWidth: '35px',
    textAlign: 'center',
    sticky: true,
    right: 34,
  },
  {
    minWidth: '35px',
    textAlign: 'center',
    sticky: true,
    right: 0,
  },
];

type InspectionItemTableProps = {
  categoryMaster: CategoryMaster[];
  setCategoryMaster: React.Dispatch<React.SetStateAction<CategoryMaster[]>>;
  typeMaster: TypeMaster[];
  setTypeMaster: React.Dispatch<React.SetStateAction<TypeMaster[]>>;
  itemMaster: ItemMaster[];
  setItemMaster: React.Dispatch<React.SetStateAction<ItemMaster[]>>;
  options: Options;
  displayMode: number;
  setDisplayMode: React.Dispatch<React.SetStateAction<number>>;
  isTableDisplay: boolean;
};

const InspectionItemTable: React.FC<InspectionItemTableProps> = ({
  categoryMaster,
  setCategoryMaster,
  typeMaster,
  setTypeMaster,
  itemMaster,
  setItemMaster,
  options,
  displayMode,
  setDisplayMode,
  isTableDisplay,
}) => {
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state.userInfo);
  const { error, hideError, showApiError, showTypeError } = useErrorModal();
  const [isLoading, setIsLoading] = useState(false);
  /** ユーザー権限 */
  const [isAdmin, setIsAdmin] = useState(false);
  /** 権限によってヘッダーを切り替える */
  const [tableHeaders, setTableHeaders] = useState(adminTableHeaders);
  /** ヘッダーref */
  const headerRef = useRef<HTMLTableRowElement>(null);
  /** 項目名固定位置 */
  const [stickyPosition, setStickyPosition] = useState(
    MANAGE_ITEM_LEFT_POSITION,
  );
  /** 削除確認モーダルの開閉状態 */
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  /** メッセージモーダルの開閉状態 */
  const [isOpenMessageModal, setIsOpenMessageModal] = useState(false);
  /** アコーディオンの開閉状態 */
  const [isOpenAccordions, setIsOpenAccordions] = useState<
    Record<string, boolean>
  >({});
  /** チェック/×アイコンの活性非活性 */
  const [isDisabledControlBtn, setIsDisabledControlBtn] = useState(false);

  /** アコーディオン操作 */
  const handleAccordion = (id: string) => {
    setIsOpenAccordions((prevOpen) => ({
      ...prevOpen,
      [id]: !prevOpen[id],
    }));
  };

  /** 大項目の操作で使用するhook */
  const {
    targetCategory,
    isOpenCategoryEditModal,
    resetTargetCategory,
    onClickNewCategory,
    onClickCategoryAddSave,
    onClickCategoryAddCancel,
    onClickCategoryEditIcon,
    onClickCategoryDeleteIcon,
    executeDeleteCategory,
    handleCategorySave,
    handleCategoryCancel,
  } = useCategory({
    categoryMaster,
    setCategoryMaster,
    setDisplayMode,
    setIsOpenDeleteModal,
    setIsDisabledControlBtn,
    setIsLoading,
    showApiError,
    showTypeError,
  });

  /** 中項目の操作で使用するhook */
  const {
    targetType,
    isOpenTypeEditModal,
    resetTargetType,
    onClickCategoryAddIcon,
    onClickTypeAddSave,
    onClickTypeAddCancel,
    onClickTypeEditIcon,
    getUploadPath,
    onClickTypeDeleteIcon,
    executeDeleteType,
    handleTypeSave,
    handleTypeCancel,
  } = useType({
    categoryMaster,
    typeMaster,
    setTypeMaster,
    setDisplayMode,
    setIsOpenDeleteModal,
    setIsDisabledControlBtn,
    isOpenAccordions,
    handleAccordion,
    setIsLoading,
    showApiError,
    showTypeError,
  });

  /** 小項目の操作で使用するhook */
  const {
    targetItem,
    isOpenItemEditModal,
    resetTargetItem,
    onClickTypeAddIcon,
    onClickItemAddIcon,
    onClickItemEditIcon,
    onClickItemDeleteIcon,
    executeDeleteItem,
    handleItemInputChange,
    checkInputForCondition,
    onClickItemEditSave,
    onClickItemEditCancel,
    onClickItemNamesEdit,
    handleItemNamesSave,
    handleItemNamesCancel,
    isSetPFRCodes,
  } = useItem({
    itemMaster,
    setItemMaster,
    setDisplayMode,
    setIsOpenDeleteModal,
    isOpenAccordions,
    handleAccordion,
    setIsLoading,
    setIsOpenMessageModal,
    setIsDisabledControlBtn,
    showApiError,
    showTypeError,
  });

  /** 並び替えのhook */
  const { SortableItem, handleDragStart, handleDragEnd } = useSortItem({
    categoryMaster,
    setCategoryMaster,
    typeMaster,
    setTypeMaster,
    itemMaster,
    setItemMaster,
    targetItem,
    displayMode,
    isOpenAccordions,
    handleAccordion,
    isSetPFRCodes,
  });

  useEffect(() => {
    if (headerRef.current) {
      // 左の表示固定している項目の固定位置をヘッダーから取得する
      let position = 0;
      const newStickPosition = MANAGE_ITEM_LEFT_POSITION;
      for (let i = 0; i < headerRef.current.children.length; i++) {
        const td = headerRef.current.children[i];
        if (3 < i) {
          // 固定外のため処理終了
          break;
        }
        if (i === 3) {
          // 小項目
          newStickPosition[3] = position;
          position = position + 35;
          newStickPosition[4] = position;
          position = position + 35;
          newStickPosition[5] = position;
          position = position + (td.clientWidth - 70);
        } else {
          newStickPosition[i] = position;
          position = position + td.getBoundingClientRect().width;
        }
      }
      setStickyPosition(newStickPosition);
    }
  }, [headerRef.current]);

  /** 権限によってヘッダーの表示項目を切り替える */
  useEffect(() => {
    setIsAdmin(isAdministrator(userData));
    if (isAreaManager(userData)) {
      // 地域管理者では表示しない項目のラベル
      const displayOffLabels = ['LBL_MANAGE_INSPECTION_OPTIONAL_ADDITION'];
      setTableHeaders(
        adminTableHeaders.filter((header) => {
          if (header.label) {
            return !displayOffLabels.includes(header.label);
          }
          return true;
        }),
      );
    }
  }, [userData]);

  /** 削除確認ダイアログOK押下時 */
  const onClickDeleteOk = async () => {
    if (targetCategory.categoryId) {
      executeDeleteCategory();
      return;
    }
    if (targetType.typeId) {
      executeDeleteType();
      return;
    }
    if (targetItem.itemId) {
      executeDeleteItem();
      return;
    }
  };

  /** 削除確認ダイアログキャンセル押下時 */
  const onClickDeleteCancel = () => {
    // 全てリセットしてモーダルを閉じる
    resetTargetCategory();
    resetTargetType();
    resetTargetItem();
    setIsOpenDeleteModal(false);
  };

  return (
    <Box>
      <TableContainer
        sx={{
          height: `calc( 100dvh - (${HEADER_HEIGHT} + 50px +  2rem) )`,
          width: '100%',
          overflow: 'auto',
        }}
        component={Paper}
        style={{
          backgroundColor: 'transparent',
        }}
      >
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
        >
          <Table sx={manageItemStyle.tableStyle}>
            <TableHead sx={manageItemStyle.headerSticky}>
              <TableRow ref={headerRef}>
                {tableHeaders.map((header, i) => (
                  <TableCell
                    key={`header_${i}`}
                    sx={[
                      TABLE_HEADER,
                      {
                        px: header.px ?? '',
                        minWidth: header.minWidth,
                        textAlign: header.textAlign ? header.textAlign : 'left',
                        position: header.sticky ? 'sticky' : '',
                        left:
                          header.sticky && header.left !== undefined
                            ? stickyPosition[header.left]
                            : '',
                        right:
                          header.sticky && header.right !== undefined
                            ? header.right
                            : '',
                      },
                    ]}
                    colSpan={header.colSpan ? header.colSpan : 1}
                  >
                    <Typography
                      sx={
                        header.wordBreak
                          ? { whiteSpace: 'pre-line' }
                          : TEXT_OVERFLOW_1LINE
                      }
                    >
                      {header.label ? t(header.label) : ''}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {isTableDisplay && (
              <TableBody>
                <SortableContext
                  id={MANAGE_ITEM_ROW_TYPE.CATEGORY}
                  items={categoryMaster.map((category) => category.categoryId)}
                  strategy={verticalListSortingStrategy}
                >
                  {categoryMaster.map((category) => {
                    // 大項目
                    return (
                      <React.Fragment key={`category_${category.categoryId}`}>
                        <SortableItem
                          key={`category_${category.categoryId}`}
                          id={category.categoryId}
                          name={MANAGE_ITEM_ROW_TYPE.CATEGORY}
                        >
                          <CategoryRow
                            category={
                              category.categoryId ===
                              MANAGE_ITEM_TEMP_ID.CATEGORY
                                ? targetCategory
                                : category
                            }
                            mode={displayMode}
                            isAdd={
                              category.categoryId ===
                              MANAGE_ITEM_TEMP_ID.CATEGORY
                            }
                            isAdmin={isAdmin}
                            stickyPosition={stickyPosition}
                            countItems={
                              typeMaster.filter(
                                (t) => t.categoryId === category.categoryId,
                              ).length +
                              itemMaster.filter((i) => {
                                if (i.categoryId === category.categoryId) {
                                  if (i.depth === 0) {
                                    return isOpenAccordions[i.typeId];
                                  } else if (i.parentItemId) {
                                    if (i.depth === 1) {
                                      return (
                                        isOpenAccordions[i.typeId] &&
                                        isOpenAccordions[i.parentItemId]
                                      );
                                    } else {
                                      // 階層2は階層1を取得
                                      const depth1 =
                                        itemMaster.find(
                                          (i2) => i2.itemId === i.parentItemId,
                                        ) ?? '';
                                      if (depth1 && depth1.parentItemId) {
                                        // この項目が親で開いている
                                        return (
                                          isOpenAccordions[depth1.typeId] &&
                                          isOpenAccordions[
                                            depth1.parentItemId
                                          ] &&
                                          isOpenAccordions[depth1.itemId]
                                        );
                                      }
                                      return false;
                                    }
                                  }
                                } else {
                                  return false;
                                }
                              }).length
                            }
                            isDisabledBtn={isDisabledControlBtn}
                            isOpenAccordion={
                              isOpenAccordions[category.categoryId]
                            }
                            onClickAccordion={handleAccordion}
                            onClickAddIcon={onClickCategoryAddIcon}
                            onClickEditIcon={onClickCategoryEditIcon}
                            onClickDeleteIcon={onClickCategoryDeleteIcon}
                            onClickOk={onClickCategoryAddSave}
                            onClickCancel={onClickCategoryAddCancel}
                          />
                        </SortableItem>
                        <TypeItems
                          // 中項目小項目共通props
                          categoryMaster={categoryMaster}
                          setCategoryMaster={setCategoryMaster}
                          typeMaster={typeMaster}
                          setTypeMaster={setTypeMaster}
                          itemMaster={itemMaster}
                          setItemMaster={setItemMaster}
                          displayMode={displayMode}
                          isAdmin={isAdmin}
                          isOpenAccordions={isOpenAccordions}
                          handleAccordion={handleAccordion}
                          stickyPosition={stickyPosition}
                          isSetPFRCodes={isSetPFRCodes}
                          isDisabledBtn={isDisabledControlBtn}
                          // 中項目props
                          category={category}
                          targetType={targetType}
                          onClickTypeAddIcon={onClickTypeAddIcon}
                          onClickTypeEditIcon={onClickTypeEditIcon}
                          onClickTypeDeleteIcon={onClickTypeDeleteIcon}
                          onClickTypeAddSave={onClickTypeAddSave}
                          onClickTypeAddCancel={onClickTypeAddCancel}
                          // 小項目props
                          options={options}
                          targetItem={targetItem}
                          onClickItemAddIcon={onClickItemAddIcon}
                          onClickItemEditIcon={onClickItemEditIcon}
                          handleItemInputChange={handleItemInputChange}
                          checkInputForCondition={checkInputForCondition}
                          onClickItemNamesEdit={onClickItemNamesEdit}
                          onClickItemEditSave={onClickItemEditSave}
                          onClickItemEditCancel={onClickItemEditCancel}
                          onClickItemDeleteIcon={onClickItemDeleteIcon}
                        />
                      </React.Fragment>
                    );
                  })}
                </SortableContext>
                {/* 大項目追加 */}
                {displayMode === MANAGE_ITEM_MODE.VIEW && (
                  <TableRow sx={manageItemStyle.doubleBorder}>
                    <TableCell
                      sx={[
                        manageItemStyle.iconCellCenter,
                        manageItemStyle.leftSideSticky,
                        { left: MANAGE_ITEM_LEFT_POSITION[0] },
                      ]}
                    >
                      <IconButton
                        size="small"
                        sx={manageItemStyle.iconCellRight}
                        onClick={onClickNewCategory}
                      >
                        <Add fontSize="small" color="primary" />
                      </IconButton>
                    </TableCell>
                    <TableCell colSpan={27} />
                  </TableRow>
                )}
              </TableBody>
            )}
          </Table>
        </DndContext>
      </TableContainer>
      <EditCategoryModal
        category={targetCategory}
        isOpen={isOpenCategoryEditModal}
        isAdmin={isAdmin}
        handleSave={handleCategorySave}
        handleCancel={handleCategoryCancel}
      />
      <EditTypeModal
        type={targetType}
        isOpen={isOpenTypeEditModal}
        isAdmin={isAdmin}
        handleSave={handleTypeSave}
        handleCancel={handleTypeCancel}
        getPath={getUploadPath}
      />
      <EditItemModal
        item={targetItem}
        isOpen={isOpenItemEditModal}
        handleSave={handleItemNamesSave}
        handleCancel={handleItemNamesCancel}
      />
      <ConfirmModal
        isOpen={isOpenDeleteModal}
        displayMessage={t('MSG_DELETE_ITEM')}
        onClickCancel={onClickDeleteCancel}
        onClickOk={onClickDeleteOk}
      />
      <MessagePFRModal
        isOpen={isOpenMessageModal}
        onClose={() => setIsOpenMessageModal(false)}
      />
      {error && <ErrorModal message={error} onClose={hideError} />}
      <Backdrop
        sx={{
          ...LOADING_BACKDROP_STYLE,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default InspectionItemTable;
